import * as React from 'react';
import CombinedBlurFilter from './combinedBlur';
import { getComputedBackdropBlur } from './getComputedBackdropBlur';
import useBackdrop, { needsExplicitBackdrop } from './useBackdrop';
import {
  prepareCombinedBlurSource,
  type PreparedCombinedBlur,
} from './prepareCombinedBlurSource';
import type { BlurSource } from './editor';
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  captureLinearLightSource,
  cropLinearLightSource,
  getLinearLightCaptureBounds,
  LinearLightLayoutPending,
  type LinearLightCaptureBounds,
} from './source';
import { getLinearLightGeometry, type LinearLightBox } from './geometry';
import useTransformHandler from '../../components/transform/useTransformHandler';
import {
  getLinearLightFontEpoch,
  getLinearLightResult,
  getLinearLightSourceSignature,
  hasLinearLightResults,
  reuseLinearLightWork,
  storeLinearLightResult,
} from './captureCache';

let nextId = 0;
const absolute = { position: 'absolute' as const, inset: 0 };
const hasRunningAnimation = (node: HTMLElement) =>
  node
    .getAnimations?.({ subtree: true })
    .some((animation) => animation.playState === 'running');
const hideNativePaint = `[data-linear-light-native="true"] * {color:transparent!important;-webkit-text-fill-color:transparent!important;background-color:transparent!important;background-image:none!important;border-color:transparent!important;box-shadow:none!important;text-shadow:none!important;caret-color:#111!important}
[data-linear-light-native="true"] svg{visibility:hidden!important}
[data-linear-light-native="true"] li::before{opacity:0!important}`;

type Capture = {
  uri: string;
  mask?: string;
  paintMask?: string;
  blur?: number;
  feather?: number;
  prepared?: PreparedCombinedBlur;
  bounds?: LinearLightCaptureBounds;
  dimensions?: { width: number; height: number };
  box?: LinearLightBox;
};
// A finished capture, published again when the same layer mounts again.
type Result = { capture: Capture; uri: string };
const toBlurSource = (
  { mask, paintMask, blur, feather }: Capture,
  combinedBlur: boolean
): BlurSource | undefined =>
  combinedBlur ? { mask, paintMask, blur, feather } : undefined;

type Props = {
  readonly children: React.ReactNode;
  readonly combinedBlur?: boolean;
  readonly backdropElementId?: string;
  readonly blendMode?: string;
  readonly opacity?: number;
  readonly paddingRatio?: number;
  readonly nativeEditor?: boolean;
  readonly stackLevel?: number;
  readonly sourceUri?: string;
  readonly sourceMask?: string;
  readonly sourcePaintMask?: string;
  readonly sourceBlur?: number;
  readonly sourceFeather?: number;
  readonly onPending?: () => () => void;
  readonly refreshKey?: unknown;
  readonly onError?: (error: Error) => void;
  readonly onReady?: (uri: string, blurSource?: BlurSource) => void;
  readonly renderEffect?: boolean;
  readonly transformElementId?: string;
  readonly rotationAngle?: number;
  readonly cropToPaint?: boolean;
  readonly opaqueBackdrop?: boolean;
  readonly canvasSize?: { width: number; height: number };
  readonly captureFonts?: boolean;
  readonly elementBox?: LinearLightBox;
  readonly isText?: boolean;
};

export default function LinearLightLayer({
  children,
  combinedBlur = false,
  backdropElementId,
  blendMode = 'normal',
  opacity = 100,
  paddingRatio = 0,
  nativeEditor = false,
  stackLevel = 0,
  sourceUri,
  sourceMask,
  sourcePaintMask,
  sourceBlur,
  sourceFeather,
  onPending,
  refreshKey,
  onError,
  onReady,
  renderEffect = true,
  transformElementId,
  rotationAngle = 0,
  cropToPaint = false,
  opaqueBackdrop = false,
  canvasSize,
  captureFonts = true,
  elementBox,
  isText = false,
}: Props) {
  const [id] = useState(() => `linear-light-${++nextId}`);
  const source = useRef<HTMLDivElement>(null);
  const offset = useRef<SVGFEOffsetElement>(null);
  const maskOffset = useRef<SVGFEOffsetElement>(null);
  const paintMaskOffset = useRef<SVGFEOffsetElement>(null);
  const backdropOffset = useRef<SVGFEOffsetElement>(null);
  const htmlEffect = useRef<HTMLDivElement>(null);
  const svgEffect = useRef<SVGSVGElement>(null);
  const backdropInput = useRef<SVGFEImageElement>(null);
  const explicitBackdrop =
    combinedBlur &&
    opacity > 0 &&
    Boolean(backdropElementId || nativeEditor || transformElementId) &&
    (nativeEditor || needsExplicitBackdrop());
  const effect = explicitBackdrop ? svgEffect : htmlEffect;
  const { uri: backdropUri, failed: backdropFailed } = useBackdrop({
    enabled: explicitBackdrop,
    source,
    effect,
    image: backdropInput,
    elementId: backdropElementId,
    onError,
  });
  const drag = useRef({ active: false, origin: [0, 0], position: [0, 0] });
  const geometry = useRef<{
    start?: LinearLightBox;
    target?: LinearLightBox;
    reflow: boolean;
    revision: number;
  }>({ reflow: false, revision: 0 });
  const currentElementBox = useRef(elementBox);
  currentElementBox.current = elementBox;
  const motionFrame = useRef(0);
  const pageSize = useRef([0, 0]);
  const opaqueRegion = useRef(false);
  const [useOpaqueBackdrop, setUseOpaqueBackdrop] = useState(false);
  const refresh = useRef<(mode?: 'invalidate' | 'reflow') => void>();
  const [capture, setCapture] = useState<Capture>(() => ({
    uri: sourceUri || '',
  }));
  const { uri, bounds: capturedBounds, dimensions } = capture;
  const canvasWidth = canvasSize?.width;
  const canvasHeight = canvasSize?.height;
  const previousCanvasSize = useRef(canvasSize);
  const bounds = useMemo(() => {
    if (!capturedBounds || !dimensions || !canvasWidth || !canvasHeight)
      return capturedBounds;
    const sx = canvasWidth / dimensions.width;
    const sy = canvasHeight / dimensions.height;
    return {
      x: capturedBounds.x * sx,
      y: capturedBounds.y * sy,
      width: capturedBounds.width * sx,
      height: capturedBounds.height * sy,
    };
  }, [capturedBounds, dimensions, canvasWidth, canvasHeight]);
  const preparedBounds = useMemo(() => {
    if (!capture.prepared) return undefined;
    const region = capture.prepared.bounds;
    const scale =
      canvasWidth && dimensions ? canvasWidth / dimensions.width : 1;
    return {
      x: (bounds?.x || 0) + region.x * scale,
      y: (bounds?.y || 0) + region.y * scale,
      width: region.width * scale,
      height: region.height * scale,
    };
  }, [capture.prepared, bounds, canvasWidth, dimensions]);
  const sourceBounds = useMemo(
    () =>
      preparedBounds
        ? {
            x: (bounds?.x || 0) - preparedBounds.x,
            y: (bounds?.y || 0) - preparedBounds.y,
            width: bounds?.width || dimensions?.width || 0,
            height: bounds?.height || dimensions?.height || 0,
          }
        : bounds,
    [preparedBounds, bounds, dimensions]
  );
  const preparedGrid =
    (capture.prepared?.grid || 1) *
    (canvasWidth && dimensions ? canvasWidth / dimensions.width : 1);
  const movePrepared = useCallback(
    (dx: number, dy: number) => {
      // Move the backdrop on the original raster grid. Offset cached paint/mask
      // within it by the remainder so the visible layer still follows precisely.
      const x = preparedGrid ? Math.floor(dx / preparedGrid) * preparedGrid : 0;
      const y = preparedGrid ? Math.floor(dy / preparedGrid) * preparedGrid : 0;
      if (effect.current)
        effect.current.style.transform = `translate(${x}px, ${y}px)`;
      offset.current?.setAttribute('dx', String(dx - x));
      offset.current?.setAttribute('dy', String(dy - y));
      maskOffset.current?.setAttribute('dx', String(dx - x));
      maskOffset.current?.setAttribute('dy', String(dy - y));
      backdropOffset.current?.setAttribute('dx', String(dx - x));
      backdropOffset.current?.setAttribute('dy', String(dy - y));
    },
    [preparedGrid, effect]
  );
  const capturedBox = useMemo(() => {
    if (!capture.box || !dimensions || !canvasWidth || !canvasHeight)
      return capture.box;
    const sx = canvasWidth / dimensions.width,
      sy = canvasHeight / dimensions.height;
    return {
      ...capture.box,
      x: capture.box.x * sx,
      y: capture.box.y * sy,
      width: capture.box.width * sx,
      height: capture.box.height * sy,
    };
  }, [capture.box, dimensions, canvasWidth, canvasHeight]);
  const [status, setStatus] = useState(sourceUri ? 'ready' : 'loading');
  // Captures are published atomically. Reuse the last complete foreground
  // during a refresh, but never conceal native paint for a partial/failed one.
  const foregroundReady = Boolean(
    uri &&
      (!combinedBlur || capture.mask) &&
      (!capture.prepared ||
        (capture.prepared.paint &&
          capture.prepared.footprint &&
          (!capture.paintMask || capture.prepared.backdrop)))
  );
  const nativeFallback =
    !foregroundReady ||
    status === 'error' ||
    (explicitBackdrop && !backdropUri);
  const updateOpaqueRegion = useCallback(
    (dx: number, dy: number, paintedBounds = bounds) => {
      // Stay inside the solid page, away from its 5px rounded corners.
      // Crossing an edge restores the general alpha-aware filter before paint.
      const inside = Boolean(
        opaqueBackdrop &&
          cropToPaint &&
          paintedBounds &&
          paintedBounds.x + dx >= 5 &&
          paintedBounds.y + dy >= 5 &&
          paintedBounds.x + dx + paintedBounds.width <=
            pageSize.current[0] - 5 &&
          paintedBounds.y + dy + paintedBounds.height <= pageSize.current[1] - 5
      );
      if (inside !== opaqueRegion.current) {
        opaqueRegion.current = inside;
        setUseOpaqueBackdrop(inside);
      }
    },
    [opaqueBackdrop, cropToPaint, bounds]
  );
  const paintPosition = useCallback(() => {
    motionFrame.current = 0;
    if (preparedBounds) {
      movePrepared(drag.current.position[0], drag.current.position[1]);
      return;
    }
    if (
      effect.current &&
      capturedBox &&
      capturedBox.width > 0 &&
      capturedBox.height > 0 &&
      bounds &&
      geometry.current.target
    ) {
      const paint = getLinearLightGeometry(
        capturedBox,
        geometry.current.target,
        bounds,
        geometry.current.reflow
      );
      effect.current.style.transformOrigin = '0 0';
      effect.current.style.transform = paint.transform;
      updateOpaqueRegion(0, 0, paint.bounds);
      return;
    }
    const [dx, dy] = drag.current.position;
    if (effect.current)
      effect.current.style.transform = `translate(${dx}px, ${dy}px)`;
    updateOpaqueRegion(dx, dy);
  }, [
    updateOpaqueRegion,
    capturedBox,
    bounds,
    preparedBounds,
    movePrepared,
    effect,
  ]);
  useLayoutEffect(() => {
    const previous = previousCanvasSize.current;
    previousCanvasSize.current = canvasSize;
    if (
      !previous ||
      !(previous.width > 0) ||
      !(previous.height > 0) ||
      !canvasWidth ||
      !canvasHeight ||
      (previous.width === canvasWidth && previous.height === canvasHeight)
    )
      return;
    const sx = canvasWidth / previous.width;
    const sy = canvasHeight / previous.height;
    drag.current.position = [
      drag.current.position[0] * sx,
      drag.current.position[1] * sy,
    ];
    drag.current.origin = [
      drag.current.origin[0] * sx,
      drag.current.origin[1] * sy,
    ];
    for (const key of ['start', 'target'] as const) {
      const box = geometry.current[key];
      if (box)
        geometry.current[key] = {
          ...box,
          x: box.x * sx,
          y: box.y * sy,
          width: box.width * sx,
          height: box.height * sy,
        };
    }
    cancelAnimationFrame(motionFrame.current);
    paintPosition();
  }, [canvasSize, canvasWidth, canvasHeight, paintPosition]);
  useTransformHandler(
    transformElementId,
    (transform) => {
      if (transform === null) {
        if (!drag.current.active) return;
        // Flush the last pointer position if the drop precedes its queued frame.
        if (motionFrame.current) {
          cancelAnimationFrame(motionFrame.current);
          paintPosition();
        }
        drag.current.active = false;
        // Keep the final offset until the dropped-position texture is installed.
        refresh.current?.();
        return;
      }
      const { translate, rotate, resize, direction } = transform;
      const box = currentElementBox.current;
      const canScale = cropToPaint && box && box.width > 0 && box.height > 0;
      if (
        !translate ||
        (resize?.some((size) => size !== 0) && !canScale) ||
        (typeof rotate === 'number' && rotate !== (rotationAngle || 0))
      )
        return;
      if (!drag.current.active) {
        drag.current.origin = [...drag.current.position];
        drag.current.active = true;
        // Invalidate a capture that may still be finishing from a previous drop.
        geometry.current.start = box && { ...box };
        refresh.current?.('invalidate');
      }
      if (canScale && geometry.current.start) {
        const start = geometry.current.start;
        geometry.current.target = {
          ...start,
          x: start.x + translate[0],
          y: start.y + translate[1],
          width: resize?.[0] || start.width,
          height: resize?.[1] || start.height,
        };
        geometry.current.reflow = Boolean(
          isText && resize?.[0] && direction?.[0] && direction?.[1] === 0
        );
        geometry.current.revision++;
        if (geometry.current.reflow) refresh.current?.('reflow');
      }
      drag.current.position = translate.map(
        (value, index) => value + drag.current.origin[index]
      );
      if (cropToPaint) {
        // Move the entire small backdrop region so it samples its new location.
        if (!motionFrame.current)
          motionFrame.current = requestAnimationFrame(paintPosition);
      } else if (preparedBounds) {
        movePrepared(drag.current.position[0], drag.current.position[1]);
      } else {
        paintMaskOffset.current?.setAttribute(
          'dx',
          String(drag.current.position[0])
        );
        paintMaskOffset.current?.setAttribute(
          'dy',
          String(drag.current.position[1])
        );
        offset.current?.setAttribute('dx', String(drag.current.position[0]));
        offset.current?.setAttribute('dy', String(drag.current.position[1]));
        maskOffset.current?.setAttribute(
          'dx',
          String(drag.current.position[0])
        );
        maskOffset.current?.setAttribute(
          'dy',
          String(drag.current.position[1])
        );
      }
    },
    [
      transformElementId,
      rotationAngle,
      cropToPaint,
      paintPosition,
      isText,
      preparedBounds,
      movePrepared,
    ]
  );
  useLayoutEffect(() => {
    cancelAnimationFrame(motionFrame.current);
    motionFrame.current = 0;
    drag.current.position = [0, 0];
    offset.current?.setAttribute('dx', '0');
    offset.current?.setAttribute('dy', '0');
    maskOffset.current?.setAttribute('dx', '0');
    maskOffset.current?.setAttribute('dy', '0');
    paintMaskOffset.current?.setAttribute('dx', '0');
    paintMaskOffset.current?.setAttribute('dy', '0');
    movePrepared(0, 0);
    if (drag.current.active && capturedBox) {
      paintPosition();
    } else {
      geometry.current.target = undefined;
      if (effect.current) effect.current.style.transform = '';
    }
    // Install/replace paint atomically; pointer frames remain imperative.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [capture]);
  useLayoutEffect(() => {
    if (opaqueBackdrop && cropToPaint)
      pageSize.current = [
        source.current?.offsetWidth || 0,
        source.current?.offsetHeight || 0,
      ];
    if (geometry.current.target) paintPosition();
    else updateOpaqueRegion(drag.current.position[0], drag.current.position[1]);
  }, [updateOpaqueRegion, opaqueBackdrop, cropToPaint, paintPosition]);
  useLayoutEffect(() => () => cancelAnimationFrame(motionFrame.current), []);
  useLayoutEffect(() => {
    if (sourceUri && !cropToPaint && !combinedBlur) {
      setCapture({ uri: sourceUri });
      setStatus('ready');
      return;
    }
    const node = source.current;
    if (!node) return;
    let active = true;
    let generation = 0;
    let frame = 0;
    let busy = false;
    let reflowTimer = 0;
    let release: (() => void) | undefined;
    // Everything outside the markup that changes what a capture produces.
    const signatureOf = () =>
      sourceUri
        ? undefined
        : getLinearLightSourceSignature(node, {
            combinedBlur,
            cropToPaint,
            transform: Boolean(transformElementId),
            nativeEditor,
            explicitBackdrop,
            captureFonts,
            canvasWidth,
            canvasHeight,
            fonts: captureFonts ? getLinearLightFontEpoch() : 0,
          });
    // A layer this session already finished paints its result before the
    // first frame instead of showing native fallback while capturing again.
    const mountSignature = hasLinearLightResults() ? signatureOf() : undefined;
    const reused = getLinearLightResult<Result>(mountSignature);
    // The signature the installed capture was made from. A request that
    // finds the same settled markup (the size observation at mount, a no-op
    // mutation) has nothing new to render.
    let publishedSignature = reused ? mountSignature : undefined;
    // A capture has been requested and has not yet published or failed.
    let pending = false;
    const request = (mode?: 'invalidate' | 'reflow') => {
      if (mode === 'invalidate') {
        generation++;
        cancelAnimationFrame(frame);
        return;
      }
      if (drag.current.active) {
        if (geometry.current.reflow && !reflowTimer && !busy) {
          reflowTimer = window.setTimeout(() => {
            reflowTimer = 0;
            captureFrame();
          }, 120);
        }
        return;
      }
      window.clearTimeout(reflowTimer);
      reflowTimer = 0;
      captureFrame();
    };
    const captureFrame = () => {
      if (
        publishedSignature &&
        !drag.current.active &&
        !drag.current.position[0] &&
        !drag.current.position[1] &&
        signatureOf() === publishedSignature
      ) {
        // Back to the installed markup (an undo, a slider dragged away and
        // back) while another capture runs: drop that capture so it cannot
        // install its pixels over the restored layer.
        if (pending) {
          pending = false;
          generation++;
          cancelAnimationFrame(frame);
          setStatus('ready');
          release?.();
          release = undefined;
        }
        return;
      }
      generation++;
      pending = true;
      cancelAnimationFrame(frame);
      release?.();
      release = onPending?.();
      setStatus('loading');
      frame = requestAnimationFrame(async () => {
        if (busy) return;
        const token = generation;
        const wasAnimating = !sourceUri && hasRunningAnimation(node);
        if (
          !node.offsetWidth ||
          !node.offsetHeight ||
          node.querySelector('[data-lut-status="loading"]')
        )
          return;
        busy = true;
        try {
          const signature = signatureOf();
          let capturedRevision = geometry.current.revision;
          let snapshotBox = drag.current.active
            ? geometry.current.target
            : currentElementBox.current;
          let capturedDimensions = {
            width: canvasWidth || node.offsetWidth,
            height: canvasHeight || node.offsetHeight,
          };
          let nextBounds: LinearLightCaptureBounds | undefined;
          const snapshotBounds = () => {
            capturedRevision = geometry.current.revision;
            snapshotBox = drag.current.active
              ? geometry.current.target
              : currentElementBox.current;
            capturedDimensions = {
              width: canvasWidth || node.offsetWidth,
              height: canvasHeight || node.offsetHeight,
            };
            nextBounds =
              combinedBlur || transformElementId || cropToPaint
                ? getLinearLightCaptureBounds(node, cropToPaint)
                : undefined;
            return nextBounds;
          };
          const next =
            sourceUri ||
            (await captureLinearLightSource(node, snapshotBounds, {
              skipFonts: !captureFonts && !node.querySelector('text'),
            }));
          if (!active || token !== generation) return;
          const marker = node.querySelector<HTMLElement>('.element-backdrop');
          const blur =
            sourceBlur ?? (marker ? getComputedBackdropBlur(marker) : 0);
          const mask = combinedBlur
            ? sourceMask ||
              (await captureLinearLightSource(node, nextBounds, {
                skipFonts: true,
                backdropMask: true,
              }))
            : undefined;
          const paintMask = combinedBlur
            ? sourcePaintMask ||
              (node.querySelector('[data-combined-paint-frame]')
                ? await captureLinearLightSource(node, nextBounds, {
                    skipFonts: true,
                    paintMask: true,
                  })
                : undefined)
            : undefined;
          const feather =
            sourceFeather ??
            (marker
              ? (parseFloat(
                  getComputedStyle(marker).getPropertyValue(
                    '--backdrop-feather-ratio'
                  )
                ) || 0) * blur
              : 0);
          if (!active || token !== generation) return;
          const texture = cropToPaint
            ? await cropLinearLightSource(
                next,
                nextBounds || {
                  x: 0,
                  y: 0,
                  width: node.offsetWidth,
                  height: node.offsetHeight,
                }
              )
            : { uri: next, bounds: nextBounds };
          if (!active || token !== generation) return;
          // Fractional density changes subpixel blur sampling during motion.
          // Retain its original filter until that preview can match exactly.
          const density = window.devicePixelRatio || 1;
          const canPrepare =
            Number.isInteger(density) && Number.isInteger(Math.log2(density));
          const prepared =
            combinedBlur &&
            mask &&
            canPrepare &&
            (transformElementId || nativeEditor)
              ? await reuseLinearLightWork(
                  // The signature determines every input of the preparation,
                  // provided the markup still matches it: blur, feather and
                  // mask were read after the snapshot.
                  signature && signatureOf() === signature
                    ? `${signature}|prepare`
                    : undefined,
                  () =>
                    prepareCombinedBlurSource(
                      texture.uri,
                      mask,
                      texture.bounds?.width || node.offsetWidth,
                      blur,
                      feather,
                      paintMask
                    )
                )
              : undefined;
          if (!active || token !== generation) return;
          const published: Capture = {
            ...texture,
            prepared,
            mask,
            paintMask,
            blur,
            feather,
            dimensions: capturedDimensions,
            box: snapshotBox,
          };
          // Only a layout that stayed settled through the whole capture is
          // worth publishing again; a drop still needs its own capture.
          publishedSignature =
            !drag.current.active && signature && signatureOf() === signature
              ? signature
              : undefined;
          if (publishedSignature)
            storeLinearLightResult<Result>(publishedSignature, {
              capture: published,
              uri: next,
            });
          setCapture(published);
          // Let feImage consume the already decoded PNG before captures/frames.
          frame = requestAnimationFrame(() => {
            frame = requestAnimationFrame(() => {
              if (!active || token !== generation) return;
              pending = false;
              setStatus('ready');
              onReady?.(next, toBlurSource(published, combinedBlur));
              release?.();
              release = undefined;
              if (drag.current.active && geometry.current.reflow) {
                if (capturedRevision !== geometry.current.revision)
                  request('reflow');
              } else if (
                wasAnimating ||
                (!sourceUri && hasRunningAnimation(node))
              ) {
                // Encoding may outlast the animation. Capture its settled frame
                // even when it is no longer running at publication time.
                request();
              }
            });
          });
        } catch (error) {
          if (error instanceof LinearLightLayoutPending && active) {
            request();
            return;
          }
          if (!active || token !== generation) return;
          // Nothing is installed now, so the next request must capture.
          pending = false;
          publishedSignature = undefined;
          setCapture({ uri: '' });
          setStatus('error');
          onError?.(new Error('Unable to render layer effect source.'));
          release?.();
          release = undefined;
        } finally {
          busy = false;
          if (active && token !== generation) request();
        }
      });
    };
    refresh.current = request;
    const observer = new MutationObserver((records) => {
      // Concealment styles belong to this wrapper, not authored paint. Ignore
      // them so switching to the compositor cannot trigger another capture.
      if (
        records.some(
          (record) =>
            record.attributeName !== 'data-linear-light-native' &&
            !(record.target === node && record.attributeName === 'style')
        )
      )
        request();
    });
    observer.observe(node, {
      subtree: true,
      childList: true,
      characterData: true,
      attributes: true,
    });
    const handleChange = () => request();
    const resize = new ResizeObserver(handleChange);
    resize.observe(node);
    document.fonts.addEventListener('loadingdone', handleChange);
    node.addEventListener('web-stories-animation-update', handleChange);
    node.addEventListener('animationend', handleChange, true);
    node.addEventListener('transitionend', handleChange, true);
    if (reused) {
      setCapture(reused.capture);
      setStatus('ready');
      onReady?.(reused.uri, toBlurSource(reused.capture, combinedBlur));
    } else {
      request();
    }
    return () => {
      active = false;
      refresh.current = undefined;
      generation++;
      cancelAnimationFrame(frame);
      window.clearTimeout(reflowTimer);
      observer.disconnect();
      resize.disconnect();
      document.fonts.removeEventListener('loadingdone', handleChange);
      node.removeEventListener('web-stories-animation-update', handleChange);
      node.removeEventListener('animationend', handleChange, true);
      node.removeEventListener('transitionend', handleChange, true);
      release?.();
    };
  }, [
    sourceUri,
    sourceMask,
    sourcePaintMask,
    sourceBlur,
    sourceFeather,
    combinedBlur,
    onPending,
    refreshKey,
    onError,
    onReady,
    transformElementId,
    cropToPaint,
    canvasWidth,
    canvasHeight,
    captureFonts,
    nativeEditor,
    explicitBackdrop,
  ]);
  const innerInset = `${(paddingRatio / (1 + 2 * paddingRatio)) * 100}%`;
  const coverage = Math.min(100, Math.max(0, opacity)) / 100;
  const effectBounds = preparedBounds || (cropToPaint ? bounds : undefined);
  const needsOffset = Boolean(
    transformElementId && !cropToPaint && !preparedBounds
  );
  return (
    <div
      data-linear-light-layer=""
      data-combined-prepared={capture.prepared ? true : undefined}
      data-combined-blur={combinedBlur ? capture.blur ?? 0 : undefined}
      data-combined-feather={combinedBlur ? capture.feather ?? 0 : undefined}
      data-combined-blend={combinedBlur ? blendMode : undefined}
      data-linear-light-opacity={coverage}
      data-linear-light-padding={paddingRatio}
      data-linear-light-status={
        backdropFailed
          ? 'error'
          : nativeFallback && status === 'ready'
          ? 'loading'
          : status
      }
      style={{
        ...absolute,
        top: `${-paddingRatio * 100}%`,
        bottom: `${-paddingRatio * 100}%`,
        zIndex: stackLevel,
        pointerEvents: 'none',
      }}
    >
      {nativeEditor && (
        <style>
          {hideNativePaint +
            (combinedBlur
              ? `[data-linear-light-native="true"] img:not([data-combined-crop-guide-image]),[data-linear-light-native="true"] canvas{opacity:0!important}
                 [data-linear-light-native="true"] .element-backdrop{visibility:hidden!important}
                 [data-linear-light-native="true"] [data-combined-crop-guide]{
                   opacity:var(--combined-layer-opacity);
                   clip-path:var(--combined-crop-guide-clip);
                 }`
              : '')}
        </style>
      )}
      <div
        ref={source}
        data-linear-light-source=""
        data-linear-light-native={
          nativeEditor && !nativeFallback ? 'true' : undefined
        }
        style={{
          ...absolute,
          ...({ '--combined-layer-opacity': coverage } as React.CSSProperties),
          opacity: nativeFallback ? coverage : nativeEditor ? 1 : 0,
          zIndex: nativeEditor ? 2 : 0,
          pointerEvents: 'none',
        }}
      >
        {paddingRatio ? (
          <div
            data-linear-light-coordinates=""
            style={{
              ...absolute,
              top: innerInset,
              bottom: innerInset,
              pointerEvents: 'none',
            }}
          >
            {children}
          </div>
        ) : (
          children
        )}
      </div>
      <svg
        aria-hidden="true"
        width="100%"
        height="100%"
        data-linear-light-explicit={explicitBackdrop ? '' : undefined}
        ref={explicitBackdrop ? svgEffect : undefined}
        style={{
          ...absolute,
          pointerEvents: 'none',
          ...(explicitBackdrop
            ? {
                zIndex: 1,
                ...(effectBounds
                  ? {
                      inset: 'auto',
                      left: effectBounds.x,
                      top: effectBounds.y,
                      width: effectBounds.width,
                      height: effectBounds.height,
                    }
                  : {}),
              }
            : {}),
        }}
      >
        <defs>
          <filter
            id={id}
            filterUnits={
              bounds || preparedBounds ? 'userSpaceOnUse' : undefined
            }
            x={preparedBounds ? 0 : cropToPaint ? 0 : bounds?.x ?? 0}
            y={preparedBounds ? 0 : cropToPaint ? 0 : bounds?.y ?? 0}
            width={preparedBounds?.width ?? bounds?.width ?? '100%'}
            height={preparedBounds?.height ?? bounds?.height ?? '100%'}
            colorInterpolationFilters="sRGB"
          >
            {explicitBackdrop && (
              <feImage
                ref={backdropInput}
                href={backdropUri || undefined}
                preserveAspectRatio="none"
                result="page-backdrop"
              />
            )}
            <feImage
              href={uri || undefined}
              x={cropToPaint ? 0 : sourceBounds?.x ?? 0}
              y={cropToPaint ? 0 : sourceBounds?.y ?? 0}
              width={sourceBounds?.width ?? '100%'}
              height={sourceBounds?.height ?? '100%'}
              preserveAspectRatio="none"
              result={
                needsOffset && !capture.prepared ? 'captured-source' : 'source'
              }
            />
            {needsOffset && !capture.prepared && (
              <feOffset
                ref={offset}
                in="captured-source"
                dx="0"
                dy="0"
                result="source"
              />
            )}
            {combinedBlur ? (
              <CombinedBlurFilter
                separateBlendAlpha={needsExplicitBackdrop()}
                backdropInput={explicitBackdrop ? 'page-backdrop' : undefined}
                mask={capture.mask}
                paintMask={capture.paintMask}
                paintMaskOffsetRef={needsOffset ? paintMaskOffset : undefined}
                backdropOffsetRef={backdropOffset}
                prepared={capture.prepared}
                preparedSize={preparedBounds}
                paintOffsetRef={capture.prepared ? offset : undefined}
                offsetRef={
                  needsOffset || capture.prepared ? maskOffset : undefined
                }
                blur={capture.blur || 0}
                feather={capture.feather || 0}
                blendMode={blendMode}
                opacity={coverage}
                bounds={sourceBounds}
              />
            ) : (
              <>
                <feComponentTransfer in="source" result="opaque">
                  <feFuncA type="linear" slope="0" intercept="1" />
                </feComponentTransfer>
                {!useOpaqueBackdrop && (
                  <feComponentTransfer in="SourceGraphic" result="backdrop">
                    <feFuncA type="linear" slope="0" intercept="1" />
                  </feComponentTransfer>
                )}
                <feComposite
                  in="opaque"
                  in2={useOpaqueBackdrop ? 'SourceGraphic' : 'backdrop'}
                  operator="arithmetic"
                  k2="2"
                  k3="1"
                  k4="-1"
                  result="blend"
                />
                <feComposite
                  in="blend"
                  in2="source"
                  operator="in"
                  result="coveredBlend"
                />
                {!useOpaqueBackdrop && (
                  <>
                    <feComposite
                      in="coveredBlend"
                      in2="SourceGraphic"
                      operator="in"
                      result="onBackdrop"
                    />
                    <feComposite
                      in="source"
                      in2="SourceGraphic"
                      operator="out"
                      result="offBackdrop"
                    />
                    <feComposite
                      in="onBackdrop"
                      in2="offBackdrop"
                      operator="arithmetic"
                      k2="1"
                      k3="1"
                    />
                  </>
                )}
                {(!cropToPaint || coverage < 1) && (
                  <feComponentTransfer>
                    <feFuncA type="linear" slope={coverage} />
                  </feComponentTransfer>
                )}
              </>
            )}
          </filter>
        </defs>
        {explicitBackdrop && !nativeFallback && renderEffect && (
          <rect width="100%" height="100%" filter={`url(#${id})`} />
        )}
      </svg>
      <div
        ref={explicitBackdrop ? undefined : htmlEffect}
        aria-hidden="true"
        data-linear-light-effect=""
        style={{
          ...absolute,
          ...(effectBounds
            ? {
                inset: 'auto',
                left: effectBounds.x,
                top: effectBounds.y,
                width: effectBounds.width,
                height: effectBounds.height,
              }
            : {}),
          pointerEvents: 'none',
          zIndex: 1,
          ...(!nativeFallback &&
          uri &&
          renderEffect &&
          coverage > 0 &&
          !explicitBackdrop
            ? {
                backdropFilter: `url(#${id})`,
                WebkitBackdropFilter: `url(#${id})`,
              }
            : {}),
        }}
      />
    </div>
  );
}
